import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

const inputCls = "border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 bg-white";

export default function Profile() {
  const { t } = useT();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ company_name: "", industry: "", country: "", company_size: "", preferred_language: "en" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        company_name: user.company_name || "",
        industry: user.industry || "",
        country: user.country || "",
        company_size: user.company_size || "",
        preferred_language: user.preferred_language || "en",
      });
    }
  }, [user]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success(t("Profile saved"));
    } catch (e) {
      toast.error(t("Could not save"));
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout title={t("Company profile")}>
      <div className="max-w-3xl bg-white border border-slate-200 rounded-xl p-8 grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2 flex items-center gap-4 pb-4 border-b border-slate-200">
          {user?.picture && <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border border-slate-200" />}
          <div>
            <p className="font-display font-extrabold text-[#0B1B3D] text-lg">{user?.name}</p>
            <p className="text-sm text-[#94A3B8] font-mono-kz">{user?.email}</p>
          </div>
        </div>
        <Field label={t("Company name")}><input data-testid="profile-company" className={inputCls} value={form.company_name} onChange={(e) => upd("company_name", e.target.value)} /></Field>
        <Field label={t("Industry")}><input data-testid="profile-industry" className={inputCls} value={form.industry} onChange={(e) => upd("industry", e.target.value)} /></Field>
        <Field label={t("Country / jurisdiction")}><input data-testid="profile-country" className={inputCls} value={form.country} onChange={(e) => upd("country", e.target.value)} /></Field>
        <Field label={t("Company size")}>
          <select data-testid="profile-size" className={inputCls} value={form.company_size} onChange={(e) => upd("company_size", e.target.value)}>
            <option value="">—</option>
            {["Solo / Founder", "Small (1-50)", "Mid (51-250)", "Large (250+)"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label={t("Preferred language")}>
          <select data-testid="profile-language" className={inputCls} value={form.preferred_language} onChange={(e) => upd("preferred_language", e.target.value)}>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
          </select>
        </Field>
        <div className="md:col-span-2 flex justify-end">
          <button data-testid="profile-save-btn" onClick={save} disabled={saving} className="bg-[#0B1B3D] disabled:opacity-50 text-white hover:bg-[#1E3A8A] px-6 py-3 rounded-lg font-bold font-display">{saving ? t("Saving…") : t("Save profile")}</button>
        </div>
      </div>
    </DashboardLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-bold text-[#0B1B3D] font-display">{label}</label>
    {children}
  </div>
);
